import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { AnyFunction, isFunction, Prettify } from '@mews/std';
import { getRepositoryQueryKey } from './getRepositoryQueryKey';
import { InvalidateQueries, useInvalidateRepositoryQueries } from './useInvalidateRepositoryQueries';

type MutationMethodOptions<RepositoryRegistry, Method extends AnyFunction, Params> = Prettify<
    Omit<UseMutationOptions<Awaited<ReturnType<Method>>, Error, Params>, 'mutationKey' | 'mutationFn'> & {
        /** use to provide custom params to the function, maps them to the format requested by the generated mutation function */
        transformParams?: (params: Params) => Parameters<Method>[0];
        /** define queries to be invalidated if the mutation is successful */
        invalidateQueries?: InvalidateQueries<RepositoryRegistry>;
    }
>;

export type MutationOptions<
    RepositoryRegistry,
    RepositoryName extends keyof RepositoryRegistry,
    MethodName extends keyof RepositoryRegistry[RepositoryName],
    Params = Parameters<
        RepositoryRegistry[RepositoryName][MethodName] extends AnyFunction
            ? RepositoryRegistry[RepositoryName][MethodName]
            : never
    >[0],
> = MutationMethodOptions<
    RepositoryRegistry,
    RepositoryRegistry[RepositoryName][MethodName] extends AnyFunction
        ? RepositoryRegistry[RepositoryName][MethodName]
        : never,
    Params
>;

export const createUseRepositoryMutation =
    <RepositoryRegistry>(useRepositoryRegistry: () => RepositoryRegistry) =>
    <
        RepositoryName extends keyof RepositoryRegistry,
        MethodName extends keyof RepositoryRegistry[RepositoryName],
        Params = Parameters<
            RepositoryRegistry[RepositoryName][MethodName] extends AnyFunction
                ? RepositoryRegistry[RepositoryName][MethodName]
                : never
        >[0],
    >(
        repositoryName: RepositoryName,
        methodName: MethodName,
        mutationOptions?: MutationOptions<RepositoryRegistry, RepositoryName, MethodName, Params>
    ) => {
        const registry = useRepositoryRegistry();
        const invalidateQueries = useInvalidateRepositoryQueries<RepositoryRegistry>();
        const {
            transformParams,
            invalidateQueries: queriesToInvalidate,
            onSuccess,
            ...options
        } = mutationOptions ?? {};

        return useMutation({
            mutationKey: getRepositoryQueryKey<RepositoryRegistry, RepositoryName, MethodName, never>(
                repositoryName,
                methodName
            ),
            // eslint-disable-next-line @typescript-eslint/require-await
            mutationFn: async (params) => {
                const method = registry[repositoryName][methodName];
                return isFunction(method)
                    ? method(transformParams ? transformParams(params) : params)
                    : Promise.reject();
            },
            onSuccess: (...args) => {
                onSuccess?.(...args);

                if (queriesToInvalidate) {
                    void invalidateQueries(queriesToInvalidate);
                }
            },
            ...options,
        });
    };
